import { useState, useEffect } from 'react';
import Button from '../../components/Button';
import { Lock, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ROUTE_CHANGE_PASSWORD } from '../../constants';
import { toggleTwoFactor } from '../../utils/apiUtils';
import { showSuccessMessage, showErrorMessage } from '@/shared/components/toast-system';
import { updateUser } from '../../redux/authSlice';
import './SecuritySettings.css';

const SecuritySettings = ({
  changePasswordRoute = ROUTE_CHANGE_PASSWORD,
  toggleTwoFactorFn = toggleTwoFactor,
  isTwoFactorLocked = false,
  twoFactorLockedText = 'Two-factor authentication is required for this account and cannot be turned off.'
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(state => state.auth.user);
  const [is2FAEnabled, setIs2FAEnabled] = useState(isTwoFactorLocked);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isTwoFactorLocked) {
      setIs2FAEnabled(true);
      return;
    }
    if (user) {
      setIs2FAEnabled(!!user.isTwoFactorEnabled);
    }
  }, [user, isTwoFactorLocked]);

  const handleToggle2FA = async () => {
    if (isTwoFactorLocked || isLoading) return;
    setIsLoading(true);
    const newValue = !is2FAEnabled;
    try {
      await toggleTwoFactorFn(newValue);
      setIs2FAEnabled(newValue);
      dispatch(updateUser({ isTwoFactorEnabled: newValue }));
      showSuccessMessage(`Two-factor authentication ${newValue ? 'enabled' : 'disabled'}.`);
    } catch (error) {
      console.error('Failed to toggle 2FA', error);
      showErrorMessage('Failed to update security settings.');
    } finally {
      setIsLoading(false);
    }
  };

  const isToggleDisabled = isLoading || isTwoFactorLocked;

  return (
    <div className="security-container">
      <div className="security-section">
        <h4 className="security-title">Password</h4>
        <p className="security-description">
          Manage your password to keep your account secure.
        </p>
        <Button
          type="button"
          className="secondary security-btn-wrapper"
          onClick={() => navigate(changePasswordRoute)}
          title="Change your account password"
        >
          <Lock size={16} />
          Change Password
        </Button>
      </div>

      <div className="security-section">
        <div className="toggle-container">
          <div>
            <h4 className="security-title">
              Two-Factor Authentication (2FA)
              {is2FAEnabled && (
                <span className="status-badge">
                  <Check size={12} /> Enabled
                </span>
              )}
            </h4>
            <p className="security-description">
              Add an extra layer of security to your account. When enabled, we'll send a code to your email when you sign in from a new device.
            </p>
            {isTwoFactorLocked && (
              <p className="security-description security-locked-note">
                <Lock size={12} /> {twoFactorLockedText}
              </p>
            )}
          </div>
          <div className="toggle-wrapper">
            <label
              className={`toggle-switch ${isToggleDisabled ? 'disabled' : ''} ${isTwoFactorLocked ? 'locked' : ''}`}
              title={isTwoFactorLocked ? twoFactorLockedText : undefined}
            >
              <input
                type="checkbox"
                className="toggle-input"
                checked={is2FAEnabled}
                onChange={handleToggle2FA}
                disabled={isToggleDisabled}
                readOnly={isTwoFactorLocked}
                aria-readonly={isTwoFactorLocked}
              />
              <span className="toggle-slider"></span>
            </label>
            <span className="toggle-status-text">
              {isLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span className="spinner-small"></span> Saving...
                </div>
              ) : isTwoFactorLocked ? (
                'On (required)'
              ) : (
                is2FAEnabled ? 'On' : 'Off'
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecuritySettings;